import React, { useState, useCallback } from 'react';
import {
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  LinearProgress,
  useMediaQuery,
  useTheme,
  CircularProgress,
  withStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Avatar from '../avatar';
import classNames from 'classnames';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import {
  MUTATE_GENERATE_UPLOAD_URL,
  MUTATE_MERCHANT_LOGO,
} from '@/services/storeService';
import { useSnackbar } from 'notistack';
import useStyles from './styles';
import { useMutation, gql } from '@apollo/client';
import images from '@/assets/images';
import { Skeleton } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { Colors, CommonFonts } from '@/theme';
import { useDropzone } from 'react-dropzone';
import ImageCrop from 'react-image-crop';
import PreLoader from '@/components/preLoader';

const StoreAvatar = ({
  currentStore,
  onRefetchStore,
  hasUpdateStore,
  storeLoading,
  widthCrop = 508,
  heightCrop = 286,
  imageSizeRequired = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [imageProperty, setImageProperty] = useState({
    file: null,
    imageRef: null,
    aspect: 16 / 9,
    isCircle: false,
    preview: currentStore?.merchant?.logo?.url,
    contentType: '',
  });
  const [crop, setCrop] = useState({
    aspect: imageProperty.aspect,
    x: 164,
    y: 94,
    unit: 'px',
    width: widthCrop,
    height: heightCrop,
  });

  const handleOpenEditModal = () => setOpenEditModal(true);
  const handleCloseEditModal = (generateUploadUrlResponse) => {
    setOpenEditModal(false);
    setIsError(false);
    setImageProperty({
      ...imageProperty,
      preview: generateUploadUrlResponse
        ? URL.createObjectURL(imageProperty.file)
        : currentStore?.merchant?.logo?.url,
      file: null,
    });
  };

  const getCroppedImage = async (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], 'crop-image');
        resolve(file);
        reject((error) => console.log(error));
      }, imageProperty.contentType);
    });
  };

  const onCropComplete = async (crop) => {
    if (imageProperty.imageRef && crop.width && crop.height) {
      const file = await getCroppedImage(
        imageProperty.imageRef,
        crop
      );
      setImageProperty({ ...imageProperty, file });
    }
  };

  const onDrop = useCallback((value) => {
    const file = value[0];
    setIsError(false);
    setProgressLoading(true);
    const objURL = URL.createObjectURL(file);

    const image = new Image();
    image.src = objURL;
    image.onload = function () {
      if (
        this.width < widthCrop &&
        this.height < heightCrop &&
        this.width * this.height < widthCrop * heightCrop
      ) {
        if (imageSizeRequired) {
          setIsError(true);
        } else {
          setImageProperty({
            ...imageProperty,
            file,
            preview: objURL,
            contentType: file.type,
          });
          setCrop({
            ...crop,
            x: 0,
            y: 0,
            width: this.width,
            height: this.height,
          });
        }
      } else {
        setImageProperty({
          ...imageProperty,
          file,
          preview: objURL,
          contentType: file.type,
        });
      }
    };
    setProgressLoading(false);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  const { merchant, merchant_id } = currentStore || {};
  const image = merchant?.logo;

  const [mutateGenerateURL] = useMutation(
    gql`
      ${MUTATE_GENERATE_UPLOAD_URL}
    `
  );

  const [mutateMerchant] = useMutation(
    gql`
      ${MUTATE_MERCHANT_LOGO}
    `
  );

  const onSave = async () => {
    setIsLoading(true);
    try {
      const payload = image?.id
        ? {
            id: image?.id,
            name: image?.name,
            content_type: imageProperty.contentType,
          }
        : {
            name: imageProperty.file.name,
            content_type: imageProperty.contentType,
          };

      const result = await mutateGenerateURL({
        variables: {
          input: payload,
        },
        ignoreResults: false,
      });
      const generateUploadUrlResponse =
        result?.data?.generateUploadUrl[0];
      if (generateUploadUrlResponse) {
        const url = generateUploadUrlResponse?.upload_url;
        const fetchingResponse = await fetch(
          new Request(url, {
            method: 'PUT',
            body: imageProperty.file,
            headers: new Headers({
              'Content-Type': imageProperty.contentType,
            }),
          })
        );

        if (fetchingResponse) {
          await mutateMerchant({
            variables: {
              merchant: {
                id: merchant_id,
                logo: {
                  id: generateUploadUrlResponse?.id,
                },
              },
            },
          }).then(async (response) => {
            if (response) {
              await onRefetchStore();
              handleCloseEditModal(generateUploadUrlResponse);
              enqueueSnackbar(t('store.logo_updated'));
            }
          });
        }
      }
    } catch (error) {
      const message = error?.message || error?.errors[0]?.message;
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.boxAvatarInfo}>
          {storeLoading ? (
            <Skeleton variant="circle" width={160} height={160} />
          ) : hasUpdateStore ? (
            <>
              <Avatar
                url={image?.thumbnail || images.emptyAddLogo}
                width={160}
                height={160}
                handleOpenEditModal={
                  hasUpdateStore ? handleOpenEditModal : () => {}
                }
              />
              <div>
                {isLoading ? (
                  <CircularProgress
                    className={classNames(
                      classes.cameraIcon,
                      isLoading ? classes.noneBackground : ''
                    )}
                  />
                ) : (
                  <IconButton
                    onClick={handleOpenEditModal}
                    className={classes.cameraIcon}
                  >
                    <CameraAltIcon size="small" />
                  </IconButton>
                )}
              </div>
            </>
          ) : (
            <Avatar
              url={image?.thumbnail || images.emptyAddLogo}
              width={160}
              height={160}
            />
          )}
        </div>
      </div>
      <Dialog
        maxWidth={'xl'}
        fullWidth
        fullScreen={fullScreen}
        onClose={isLoading ? () => {} : handleCloseEditModal}
        aria-labelledby={`{edit-logo-dialog-title}`}
        open={openEditModal}
        disableBackdropClick
      >
        <DialogTitle
          id={`{edit-logo-dialog-title}`}
          onClose={() => handleCloseEditModal()}
        >
          {t('store.update_merchant_logo')}
        </DialogTitle>
        <DialogContent>
          <>
            {isError && (
              <Typography className={classes.errorText}>
                {t('image.dimension_error', {
                  width: widthCrop,
                  height: heightCrop,
                })}
              </Typography>
            )}
            {!imageProperty.preview ? (
              <div className={classes.boxImageUpload}>
                <Typography className={classes.titleUploadImage}>
                  {t('image.upload_image')}
                </Typography>
                {!progressLoading ? (
                  <div
                    {...getRootProps()}
                    className={classes.dropZone}
                  >
                    <input {...getInputProps()} />
                    <Typography className={classes.titleDrop}>
                      {t('image.drop_title')}
                    </Typography>
                    <Typography className={classes.titleBrowse}>
                      {t('image.browse')}
                    </Typography>
                  </div>
                ) : (
                  <div className={classes.progressBar}>
                    <LinearProgress />
                  </div>
                )}
                {isLoading && <PreLoader size={30} />}
              </div>
            ) : (
              <div className={classes.boxImagePreview}>
                {!imageProperty.file ? (
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <img
                      className={classes.imagePreview}
                      src={imageProperty.preview}
                    />
                  </div>
                ) : (
                  <ImageCrop
                    circularCrop={imageProperty.isCircle}
                    src={imageProperty.preview}
                    crop={crop}
                    locked
                    onChange={(newCrop) => setCrop(newCrop)}
                    onComplete={onCropComplete}
                    onImageLoaded={(image) =>
                      setImageProperty({
                        ...imageProperty,
                        imageRef: image,
                      })
                    }
                  />
                )}
              </div>
            )}
            {imageProperty.preview && (
              <Typography
                className={classes.changeImage}
                onClick={() => {
                  setImageProperty({
                    ...imageProperty,
                    file: null,
                    preview: null,
                  });
                }}
              >
                {t('image.change_image')}
              </Typography>
            )}

            {isLoading && <PreLoader size={30} />}
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseEditModal()}>
            {t('settings.cancel')}
          </Button>
          <Button
            disabled={isLoading || isError}
            onClick={onSave}
            type="submit"
            color="primary"
          >
            {t('settings.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DialogTitle = withStyles((theme) => ({
  root: {
    margin: `0 auto`,
    padding: theme.spacing(2),
    ...CommonFonts.grayBoldFont20,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: theme.palette.grey[500],
    backgroundColor: Colors.GRAY_LIGHT,
    '&:hover': {
      backgroundColor: Colors.GRAY_BORDER1_25,
    },
  },
}))((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle
      disableTypography
      className={classes.root}
      {...other}
    >
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(13),
    paddingRight: theme.spacing(13),
    paddingTop: theme.spacing(5),
    paddingBottom: 0,
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: `${theme.spacing(1)}px ${theme.spacing(4)}px`,
  },
}))(MuiDialogActions);

export default StoreAvatar;
